import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { HardHat, Check, MapPin, PlusCircle } from "lucide-react";

const COUNTRIES = ["", "Botswana", "Namibia", "South Africa", "Zambia", "Zimbabwe"];

export default function BuildersPage() {
  const [builders, setBuilders] = useState([]);
  const [country, setCountry] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const q = country ? `?country=${encodeURIComponent(country)}` : "";
        const { data } = await api.get(`/builders${q}`);
        setBuilders(data.builders || []);
      } finally { setLoading(false); }
    })();
  }, [country]);

  return (
    <main className="max-w-6xl mx-auto px-4 md:px-8 py-14 md:py-20" data-testid="builders-page">
      <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Verified Builders Directory</span></div>
      <h1 className="font-display text-4xl md:text-5xl mv-green-text mt-3">
        Local Builders — <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>independently checked.</span>
      </h1>
      <p className="mt-4 max-w-2xl text-[15px]" style={{ color: "var(--mv-muted)" }}>
        Vetted for fair wages, quality workmanship and transparent pricing. Use this list to build your MAVULA property with
        contractors we trust.
      </p>

      <div className="mt-6">
        <Link to="/apply-builder" className="btn-gold" data-testid="apply-builder-cta">
          <PlusCircle size={16}/> Apply to be listed
        </Link>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {COUNTRIES.map((c) => (
          <button
            key={c || "all"}
            onClick={() => setCountry(c)}
            className={`px-4 py-1.5 rounded-full text-[13px] font-medium border transition-colors ${
              country === c ? "mv-green-bg text-white border-transparent" : "bg-white mv-hairline hover:bg-black/5"
            }`}
            data-testid={`builders-filter-${c || "all"}`}
          >{c || "All countries"}</button>
        ))}
      </div>

      <div className="mt-10">
        {loading ? (
          <div className="text-neutral-500">Loading directory…</div>
        ) : builders.length === 0 ? (
          <div className="mv-card p-8 flex items-start gap-4" style={{ background: "linear-gradient(180deg, #fff, rgba(212,175,55,0.06))" }}>
            <HardHat className="mv-green-text shrink-0" size={26}/>
            <div>
              <div className="font-display text-xl mv-green-text">Directory being verified.</div>
              <div className="text-[14px] mt-2" style={{ color: "var(--mv-muted)" }}>
                We only publish builders who have been independently vetted for fair wages, quality work and transparent
                pricing. If you are a builder who wants to be listed — contact us via WhatsApp +44 7424 311882.
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="builders-list">
            {builders.map((b) => (
              <div key={b.id} className="mv-card p-6 h-full">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-display text-xl mv-green-text">{b.name}</div>
                    <div className="text-[13.5px]" style={{ color: "var(--mv-muted)" }}>{b.company}</div>
                  </div>
                  {b.fair_wage_verified && (
                    <span className="text-[11px] px-2 py-1 rounded-full flex items-center gap-1"
                          style={{ background: "rgba(37,211,102,0.12)", color: "#0A7A2C" }}>
                      <Check size={12}/> Fair-Wage
                    </span>
                  )}
                </div>
                <div className="mt-3 text-[13.5px] flex items-center gap-1.5" style={{ color: "var(--mv-muted)" }}>
                  <MapPin size={14}/> {b.city ? `${b.city}, ` : ""}{b.country}
                </div>
                {b.specialties && <div className="mt-2 text-[13.5px]">{b.specialties}</div>}
                {b.contact && <div className="mt-3 text-[13px] font-semibold mv-green-text">{b.contact}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
